// src/hooks/useContainerRentalApi.ts
//
// API hook for the Container Rental Manager feature. Mirrors the existing
// useEstimateApi pattern: same useApi() wrapper for auth tokens, same
// env-driven base URL, flat async/await style. Blob downloads use the same
// approach as useEstimateApi (call apiFetch and read res.blob()).

import { useApi } from "./useApi.ts";
import {
  ContainerRentalDto,
  ContainerRentalFormInput,
  RentalListParams,
  RentalListResponse,
  IssueRoundPayload,
  SkipRoundPayload,
  CancelRoundPayload,
  ExtendRentalPayload,
} from "../contracts/container_rental/types";
import type {
  TimelineParams,
  TimelineResponse,
} from "../contracts/container_rental/timeline";

const API_BASE = process.env.REACT_APP_API_BASE as string;

// Pull a filename out of a Content-Disposition header. Prefers the RFC 5987
// `filename*=UTF-8''<percent-encoded>` form (Go's stdlib emits this for any
// non-ASCII filename, e.g. Korean), falling back to the plain `filename=`
// parameter and finally to the caller-supplied default.
function parseFilenameFromCD(cd: string | null, fallback: string): string {
  if (!cd) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

export const useContainerRentalApi = () => {
  const { apiFetch } = useApi();

  /* ---------- list / detail ---------- */

  const listRentals = async (
    params: RentalListParams
  ): Promise<RentalListResponse> => {
    const qs = new URLSearchParams();
    if (params.q) qs.append("q", params.q);
    if (params.status) qs.append("status", params.status);
    if (params.page !== undefined) qs.append("page", String(params.page));
    if (params.size !== undefined) qs.append("size", String(params.size));
    if (params.sort) qs.append("sort", params.sort);

    const suffix = qs.toString();
    const url = suffix
      ? `${API_BASE}/container-rental?${suffix}`
      : `${API_BASE}/container-rental`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as RentalListResponse;
  };

  /**
   * 간트/타임라인 전용 슬림 조회.
   * q/status 는 리스트·엑셀과 동일한 의미. from/to 는 "YYYY-MM-DD" 이며
   * 서버는 둘 다 있을 때만 기간 겹침 필터를 적용한다(계약 확정 사항).
   */
  const getTimeline = async (
    params: TimelineParams = {}
  ): Promise<TimelineResponse> => {
    const qs = new URLSearchParams();
    if (params.q) qs.append("q", params.q);
    if (params.status) qs.append("status", params.status);
    if (params.from) qs.append("from", params.from);
    if (params.to) qs.append("to", params.to);

    const suffix = qs.toString();
    const url = suffix
      ? `${API_BASE}/container-rental/timeline?${suffix}`
      : `${API_BASE}/container-rental/timeline`;

    const res = await apiFetch(url);
    if (!res.ok) {
      let message = `server ${res.status}`;
      try {
        const errData = await res.json();
        if (errData && errData.error) message = errData.error;
      } catch {
        /* ignore non-JSON error bodies */
      }
      throw new Error(message);
    }
    return (await res.json()) as TimelineResponse;
  };

  const getRental = async (id: string): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${id}`);
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  /* ---------- create / update / delete ---------- */

  const createRental = async (
    form: ContainerRentalFormInput
  ): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  const updateRental = async (
    form: ContainerRentalFormInput & { id: string }
  ): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${form.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  const deleteRental = async (id: string): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      let message = `삭제 실패 (${res.status})`;
      try {
        const errData = await res.json();
        if (errData && errData.error) message = errData.error;
      } catch {
        /* ignore non-JSON error bodies */
      }
      throw new Error(message);
    }
  };

  /* ---------- per-round actions ---------- */

  const issueRound = async (
    id: string,
    round: number,
    payload: IssueRoundPayload
  ): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/container-rental/${id}/schedules/${round}/issue`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) throw new Error(`server ${res.status}`);
  };

  const skipRound = async (
    id: string,
    round: number,
    payload?: SkipRoundPayload
  ): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/container-rental/${id}/schedules/${round}/skip`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload ?? {}),
      }
    );
    if (!res.ok) throw new Error(`server ${res.status}`);
  };

  const cancelRound = async (
    id: string,
    round: number,
    payload?: CancelRoundPayload
  ): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/container-rental/${id}/schedules/${round}/cancel`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload ?? {}),
      }
    );
    if (!res.ok) throw new Error(`server ${res.status}`);
  };

  /* ---------- lifecycle actions ---------- */

  const closeRental = async (id: string): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${id}/close`, {
      method: "POST",
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  const reopenRental = async (id: string): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${id}/reopen`, {
      method: "POST",
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  const extendRental = async (
    id: string,
    payload: ExtendRentalPayload
  ): Promise<ContainerRentalDto> => {
    const res = await apiFetch(`${API_BASE}/container-rental/${id}/extend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return (await res.json()) as ContainerRentalDto;
  };

  /* ---------- excel downloads ---------- */

  const downloadRentalExcel = async (
    id: string
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiFetch(
      `${API_BASE}/container-rental/${id}/export/excel`
    );
    if (!res.ok) throw new Error(`server ${res.status}`);
    const blob = await res.blob();
    const filename = parseFilenameFromCD(
      res.headers.get("Content-Disposition"),
      `container-rental-${id}.xlsx`
    );
    return { blob, filename };
  };

  const downloadAllRentalsExcel = async (
    params: Pick<RentalListParams, "q" | "status">
  ): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    if (params.q) qs.append("q", params.q);
    if (params.status) qs.append("status", params.status);
    const suffix = qs.toString();
    const url = suffix
      ? `${API_BASE}/container-rental/export/excel?${suffix}`
      : `${API_BASE}/container-rental/export/excel`;

    const res = await apiFetch(url);
    if (!res.ok) throw new Error(`server ${res.status}`);
    const blob = await res.blob();
    const filename = parseFilenameFromCD(
      res.headers.get("Content-Disposition"),
      "container-rentals.xlsx"
    );
    return { blob, filename };
  };

  return {
    listRentals,
    getTimeline,
    getRental,
    createRental,
    updateRental,
    deleteRental,
    issueRound,
    skipRound,
    cancelRound,
    closeRental,
    reopenRental,
    extendRental,
    downloadRentalExcel,
    downloadAllRentalsExcel,
  };
};

export default useContainerRentalApi;
